import { Component, inject, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MessageService } from "@openng/optimus-ui/api";
import { FileSelectEvent, FileUploadModule } from "@openng/optimus-ui/fileupload";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { SelectModule } from "@openng/optimus-ui/select";
import { MediaService } from "../../../../../services/api/media/media.service";
import { FileData } from "../../../../../models/shared/fileData";
import { Image } from "../../../../../models/ui/image";
import { DropdownOption } from "../../../../../models/ui/dropdownOption";

@Component({
  standalone: true,
  selector: "app-file",
  imports: [FileUploadModule, ReactiveFormsModule, TranslateModule, ButtonComponent, InputTextModule, SelectModule],
  templateUrl: "./file.component.html",
  styleUrl: "./file.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class FileComponent implements OnInit {
  private messageService = inject(MessageService);
  private config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);
  private mediaService = inject(MediaService);
  public translate = inject(TranslateService);

  public form!: FormGroup;
  public photoTypes: DropdownOption[] = [
    {
      label: this.translate.instant("gate_administration.file_upload_form.photo_types.gate_outside"),
      value: "gate_outside",
    },
    {
      label: this.translate.instant("gate_administration.file_upload_form.photo_types.gate_inside"),
      value: "gate_inside",
    },
    {
      label: this.translate.instant("gate_administration.file_upload_form.photo_types.control"),
      value: "control",
    },
    {
      label: this.translate.instant("gate_administration.file_upload_form.photo_types.motor"),
      value: "motor",
    },
    {
      label: this.translate.instant("gate_administration.file_upload_form.photo_types.suspension"),
      value: "suspension",
    },
    {
      label: this.translate.instant("gate_administration.file_upload_form.photo_types.name_plate"),
      value: "name_plate",
    },
    {
      label: this.translate.instant("gate_administration.file_upload_form.photo_types.other"),
      value: "other",
    },
  ];

  public image!: Image;
  private selectedFile!: File;
  private actionType: "add" | "edit" = this.config.data.image ? "edit" : "add";

  ngOnInit() {
    if (this.config.data.image) {
      this.image = this.config.data.image;
    }
    if (this.config.data.availableTypes) {
      this.photoTypes = this.photoTypes.filter((type) => this.config.data.availableTypes.includes(type.value));
    }
    this.setupForm();
  }

  /**
   * Sets up the form for the file upload component.
   *
   * If there is an image in the component configuration, its label value is used to set the initial photo type and label.
   * If the image is a predefined gate image, its label value is used as the photo type, otherwise the photo type is set to "other".
   */
  private setupForm() {
    const initialValues = {
      photoType: this.photoTypes[0].value,
      label: "",
    };
    if (this.image) {
      if (this.mediaService.isPredefindedGateImage(this.image.labelValue)) {
        initialValues.photoType = this.image.labelValue;
      } else {
        initialValues.photoType = "other";
        initialValues.label = this.image.labelValue;
      }
    }
    this.form = new FormGroup({
      photoType: new FormControl(initialValues.photoType, Validators.required),
      label: new FormControl(initialValues.label),
    });
  }

  /**
   * Called when the user selects a new image to upload.
   * @param event The event that triggered the function call, containing the selected file.
   */
  public onFileSelect(event: FileSelectEvent) {
    const file = event.files[0];
    this.selectedFile = file;
  }

  /**
   * Closes the dialog.
   */
  public closeDialog() {
    this.ref.close();
  }

  /**
   * Saves the photo label data and closes the dialog.
   * Takes either photoType or label as photoLabel depending on whether photoType is 'other' or not.
   */
  public save() {
    const photoType = this.form?.get("photoType")?.value;
    const label = this.form?.get("label")?.value;
    const photoLabel = photoType === "other" ? label : photoType;
    if (photoType === "other" && !label) {
      this.messageService.add({
        key: "global",
        severity: "error",
        summary: this.translate.instant("common.error"),
        detail: this.translate.instant("gate_administration.file_upload_form.missing_label"),
      });
      return;
    }

    const fileData: FileData = { label: photoLabel, file: this.selectedFile };
    if (this.actionType === "edit" && this.image) {
      // File is not necessary
      fileData.fileId = this.image.id;
    }
    if (this.actionType === "add" && !this.selectedFile) {
      this.messageService.add({
        key: "global",
        severity: "error",
        summary: this.translate.instant("common.error"),
        detail: this.translate.instant("gate_administration.file_upload_form.missing_file"),
      });
      return;
    }
    this.ref.close(fileData);
  }
}
